import React from 'react';
import {
  styled,
  Box,
  TablePaginationProps,
  Button,
  Typography,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  GridRowsProp,
  useGridApiContext,
  useGridSelector,
  GridColDef,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';
import { ReactComponent as ArrowLeftIcon } from '../../images/icons/arrow_left.svg';
import { ReactComponent as ArrowRightIcon } from '../../images/icons/arrow_right.svg';
import CustomerCareSelect from './CustomerCareSelect';
import { lightBlack, lightPurple } from '../../mainTheme';

// Styled DataGrid
const StyledDataGrid = styled(DataGrid)({
  borderRadius: '16px',
  overflow: 'hidden',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F6F6F9',
    color: lightBlack,
  },
  '& .MuiDataGrid-cell': {
    color: lightBlack,
    fontWeight: 500,
    fontSize: '16px',
  },
  // Style for bolding the sorted column header
  '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '18px',
  },
});

function CustomPagination(props: any) {
  const apiRef = useGridApiContext();

  const handlePageChange = (_event: any, newPage: number) => {
    apiRef.current.setPage(newPage);
  };

  return <Pagination {...props} onPageChange={handlePageChange} />;
}

function Pagination({
  className,
}: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) {
  const apiRef = useGridApiContext();

  const handlePageChange = (_event: any, newPage: number) => {
    apiRef.current.setPage(newPage);
  };

  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const currentPage = useGridSelector(apiRef, gridPageSelector);

  // Only show pagination controls if more than one page
  const showPagination = pageCount > 1;

  const handleDropdownChange = (event: SelectChangeEvent<number>) => {
    const page = Number(event.target.value);
    if (!isNaN(page)) {
      apiRef.current.setPage(page);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: !showPagination ? 'center' : 'space-between',
        width: '100%',
      }}
    >
      {showPagination && (
        <Box
          sx={{
            marginLeft: 2,
            border: '1px solid #F2F2F2',
            borderRadius: '5px 0 0 5px',
            px: 1,
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            height: '33px',
          }}
        >
          {/* Previous Button */}
          <Button
            onClick={() => handlePageChange(null, currentPage - 1)}
            disabled={currentPage === 0}
            sx={{
              minWidth: 0,
              px: 1,
              color: currentPage === 0 ? '#BDBDBD' : '#2A2A30',
              fontWeight: 500,
              fontSize: '14px',
              textTransform: 'none',
              background: 'none',
              boxShadow: 'none',
              alignItems: 'center',
              gap: '10px',
              display: 'flex',
              '&:hover': { background: 'none' },
            }}
          >
            <ArrowLeftIcon style={{ width: 12, height: 12 }} />
            Previous
          </Button>

          {/* Page Numbers */}
          <MuiPagination
            color="secondary"
            className={className}
            count={pageCount}
            page={currentPage + 1}
            onChange={(_, newPage) => {
              handlePageChange(null, newPage - 1);
            }}
            showFirstButton={false}
            showLastButton={false}
            hidePrevButton
            hideNextButton
            siblingCount={2}
            boundaryCount={1}
            shape="rounded"
            size="medium"
            sx={{
              '& .MuiPaginationItem-root': {
                fontWeight: 500,
                fontSize: '14px',
                color: lightBlack,
                border: 'none',
                margin: '0 0px',
                transition: 'background 0.2s',
              },
              '& .Mui-selected': {
                backgroundColor: lightPurple,
                color: '#fff',
                minWidth: '25px',
                height: '30px',
                padding: '0 10px',
              },
              '& .MuiPagination-ul': {
                flexWrap: 'nowrap',
              },
            }}
          />

          {/* Next Button */}
          <Button
            onClick={() => handlePageChange(null, currentPage + 1)}
            disabled={currentPage === pageCount - 1}
            sx={{
              minWidth: 0,
              px: 1,
              color: currentPage === pageCount - 1 ? '#BDBDBD' : '#2A2A30',
              fontWeight: 500,
              fontSize: '14px',
              textTransform: 'none',
              background: 'none',
              boxShadow: 'none',
              alignItems: 'center',
              gap: '10px',
              display: 'flex',
              '&:hover': { background: 'none' },
            }}
          >
            Next
            <ArrowRightIcon style={{ width: 12, height: 12 }} />
          </Button>
        </Box>
      )}
      <Box
        mr={showPagination ? 2 : 0}
        sx={{ display: 'flex', alignItems: 'center', gap: ' 10px ' }}
      >
        <Typography
          sx={{
            color: '#1B1B1B',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            letterSpacing: '0.2px',
          }}
        >
          Page
        </Typography>
        {pageCount > 1 ? (
          <CustomerCareSelect
            value={currentPage}
            onChange={(e) =>
              handleDropdownChange(e as SelectChangeEvent<number>)
            }
            disabled={pageCount <= 1}
            renderSelectedValue={(value) => value + 1}
            sx={{
              display: 'flex',
              height: '30px',
              fontSize: '14px',
              fontWeight: 500,
              pr: '10px',
              mt: '1px',
              alignItems: 'center',
            }}
          >
            {Array.from({ length: pageCount }, (_, i) => (
              <MenuItem key={i} value={i}>
                {i + 1}
              </MenuItem>
            ))}
          </CustomerCareSelect>
        ) : (
          <Box
            sx={{
              display: 'flex',
              height: '30px',
              padding: '10px',
              alignItems: 'center',
              gap: '10px',
              borderRadius: '5px',
              border: '1px solid #F0F0F0',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {currentPage + 1}
          </Box>
        )}
        <Typography
          sx={{
            color: '#1B1B1B',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            letterSpacing: '0.2px',
          }}
        >
          {`of ${pageCount}`}
        </Typography>
      </Box>
    </Box>
  );
}

// Reusable DashboardDetailsTable Component
interface DataGridTableProps {
  rows: GridRowsProp;
  columns: GridColDef[];
  height?: string;
  maxHeight?: string;
  pageSize?: number;
  loading?: boolean;
  minHeight?: string;
  hideFooter?: boolean;
  onViewAll?: () => void;
  defaultSortField: string;
  defaultSortDirection: 'asc' | 'desc';
  hideViewAllButton?: boolean;
  onRowClick?: (row: any) => void;
  idField?: string;
}

const CustomerCareDTable: React.FC<DataGridTableProps> = ({
  rows,
  columns,
  height = '100%',
  maxHeight = '64vh',
  pageSize,
  loading = false,
  minHeight,
  hideFooter,
  onViewAll,
  defaultSortField,
  defaultSortDirection,
  hideViewAllButton,
  onRowClick,
  idField,
}) => {
  // Add sortable property to the columns you want to be sortable
  const columnsWithSorting = columns.map((col) => ({
    ...col,
    sortable: true,
  }));

  return (
    <Box height={height} maxHeight={maxHeight} py={2} minHeight={minHeight}>
      <StyledDataGrid
        rows={rows}
        columns={columnsWithSorting}
        hideFooterSelectedRowCount
        loading={loading}
        slots={{
          pagination: CustomPagination,
        }}
        onRowClick={(params) => onRowClick && onRowClick(params.row)}
        getRowId={idField ? (row) => row[idField] : undefined}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page: 0 },
          },
          sorting: {
            sortModel: [
              {
                field: defaultSortField,
                sort: defaultSortDirection,
              },
            ],
          },
        }}
        hideFooter={hideFooter}
        sx={{
          cursor: onRowClick ? 'pointer' : 'default',
        }}
      />
      {!hideViewAllButton && (
        <Button
          sx={{
            mt: 2,
            display: 'flex',
            width: '96px',
            height: '40px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '20px',
            flexShrink: 0,
            borderRadius: '30px',
            border: `1px solid ${lightPurple}`,
            background: '#FFF',
            color: lightPurple,
            fontWeight: 500,
            fontSize: '16px',
          }}
          variant="outlined"
          onClick={onViewAll}
        >
          View all
        </Button>
      )}
    </Box>
  );
};

export default CustomerCareDTable;
